import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Category, Product, ApiResponse, PaginatedResponse } from '@/types'
import { RootState } from '@/app/store'
import brandManager from '@/services/brandManager'
import { buildEndpoint, getPlatformHeader } from '@/utils/apiUtils'
import { getAuthToken } from '@/utils/secureStorage'

// Helper function to unwrap API response and remove static fields
function unwrapResponse(response: any): any {
  // If response has the wrapper structure {message, descriptiveMessage, data}, return just data
  if (response && typeof response === 'object' && 'data' in response && 'message' in response) {
    const unwrapped = response.data
    
    // Check if unwrapped data has categories array
    if (unwrapped && typeof unwrapped === 'object' && 'categories' in unwrapped && Array.isArray(unwrapped.categories)) {
      return unwrapped.categories
    }
    
    // If unwrapped data is already an array, return it
    if (Array.isArray(unwrapped)) {
      return unwrapped
    }
    
    return unwrapped
  }
  
  // If response is already an array, return it
  if (Array.isArray(response)) {
    return response
  }
  
  // Otherwise return response as-is
  return response
}

// Base API configuration
const baseQuery = fetchBaseQuery({
  baseUrl: brandManager.apiUrl,
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    const language = (getState() as RootState).settings.settings.language
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    
    headers.set('lang', language)
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    headers.set('platform', getPlatformHeader())
    
    return headers
  },
})

// Menu API slice
export const menuApi = createApi({
  reducerPath: 'menuApi',
  baseQuery,
  tagTypes: ['Categories', 'Products'],
  endpoints: (builder) => ({
    // Get all categories
    getCategories: builder.query<ApiResponse<Category[]>, void>({
      query: () => '/menu/categories',
      providesTags: ['Categories'],
    }),
    
    // Get products by category
    getProducts: builder.query<ApiResponse<Product[]>, string | number | undefined>({
      query: (categoryId) => ({
        url: '/menu/products',
        params: categoryId ? { category_id: categoryId } : {},
      }),
      providesTags: ['Products'],
    }),
    
    // Get all products
    getAllProducts: builder.query<ApiResponse<Product[]>, void>({
      query: () => '/menu/products/all',
      providesTags: ['Products'],
    }),
    
    // Search products with enhanced query parameters
    searchProducts: builder.query<ApiResponse<Product[]>, { query: string; category_id?: string | number; limit?: number }>({
      query: ({ query, category_id, limit = 20 }) => ({
        url: '/menu/products/search',
        params: { 
          q: query, 
          category_id: category_id || undefined,
          limit 
        },
      }),
      providesTags: ['Products'],
    }),
    
    // Get products by category with pagination
    getProductsByCategory: builder.query<ApiResponse<Product[]>, { category_id: string | number; page?: number; limit?: number }>({
      query: ({ category_id, page = 1, limit = 20 }) => ({
        url: `/menu/categories/${category_id}/products`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { category_id }) => [{ type: 'Products', id: category_id }],
    }),
    
    // Get detailed product information
    getProductDetails: builder.query<ApiResponse<Product & { 
      sizes?: Array<{ id: number; name: string; name_en?: string; price: number }>
      additions?: Array<{ id: number; name: string; name_en?: string; price: number }>
      nutrition?: any
      allergens?: string[]
    }>, string | number>({
      query: (productId) => `/menu/products/${productId}/details`,
      providesTags: (_result, _error, id) => [{ type: 'Products', id }],
    }),
    
    // Get featured products
    getFeaturedProducts: builder.query<ApiResponse<Product[]>, void>({
      query: () => '/menu/products/featured',
      providesTags: ['Products'],
    }),
    
    // Get popular products
    getPopularProducts: builder.query<ApiResponse<Product[]>, void>({
      query: () => '/menu/products/popular',
      providesTags: ['Products'],
    }),
    
    // Update product availability
    updateProductAvailability: builder.mutation<ApiResponse<Product>, { id: string | number; is_available: boolean }>({
      query: ({ id, is_available }) => ({
        url: `/menu/products/${id}/availability`,
        method: 'PUT',
        body: { is_available },
      }),
      invalidatesTags: ['Products'],
    }),
    
    // Update product price
    updateProductPrice: builder.mutation<ApiResponse<Product>, { id: string | number; price: number }>({
      query: ({ id, price }) => ({
        url: `/menu/products/${id}/price`,
        method: 'PUT',
        body: { price },
      }),
      invalidatesTags: ['Products'],
    }),
    
    // Get products with pagination
    getProductsPaginated: builder.query<PaginatedResponse<Product>, { page?: number; per_page?: number; category_id?: string | number }>({
      query: ({ page = 1, per_page = 20, category_id }) => ({
        url: '/menu/products/paginated',
        params: { page, per_page, category_id },
      }),
      providesTags: ['Products'],
    }),
    
    // Get category with products
    getCategoryWithProducts: builder.query<ApiResponse<Category & { products: Product[] }>, string | number>({
      query: (categoryId) => `/menu/categories/${categoryId}/products`,
      providesTags: (_result, _error, id) => [{ type: 'Categories', id }],
    }),
    
    // Get complete menu for cashier - returns categories with products
    getCashierMenu: builder.query<
      Array<Category & { products: Product[] }>,
      { branchId: string | number; typeId: string | number; isCustomerService?: boolean }
    >({
      query: ({ branchId, typeId, isCustomerService = false }) => {
        // Use customerService endpoint for CS users, cashier endpoint for regular users
        const basePath = isCustomerService 
          ? `/customerService/menus/branches/${branchId}`
          : `/cashier/menus/branches/${branchId}`
        return {
          url: buildEndpoint(basePath),
          params: { typeId },
        }
      },
      transformResponse: unwrapResponse,
      providesTags: ['Categories', 'Products'],
    }),
    
    // Get detailed product information for cashier
    getCashierProductDetails: builder.query<
      {
        id: number
        name: string
        description?: string
        image?: string
        price: string
        meals: Array<{
          id: number
          calories: string
          size: { id: number; name: string }
          price: string
          item_id: number
          category: { id: number; name: string }
          additionalsGroup: any[]
        }>
      },
      { productId: string | number; branchId: string | number }
    >({
      query: ({ productId, branchId }) => ({
        url: buildEndpoint(`/cashier/products/${productId}`),
        params: { branchId },
      }),
      transformResponse: (response: any) => {
        // Handle API response structure: { message, descriptiveMessage, data }
        if (response && typeof response === 'object' && 'data' in response) {
          return response.data
        }
        return response
      },
      providesTags: (_result, _error, { productId }) => [{ type: 'Products', id: productId }],
    }),
  }),
})

export const {
  useGetCategoriesQuery,
  useGetProductsQuery,
  useGetAllProductsQuery,
  useGetProductDetailsQuery,
  useSearchProductsQuery,
  useGetProductsByCategoryQuery,
  useGetFeaturedProductsQuery,
  useGetPopularProductsQuery,
  useUpdateProductAvailabilityMutation,
  useUpdateProductPriceMutation,
  useGetProductsPaginatedQuery,
  useGetCategoryWithProductsQuery,
  useGetCashierMenuQuery,
  useGetCashierProductDetailsQuery,
} = menuApi
